import React, { useCallback, useContext, useEffect, useState, useMemo } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { createPortal } from 'react-dom';
import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  ListItemText,
  OutlinedInput,
  Checkbox,
  Select,
  FormControl,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import ItemisedBillReportService from 'services/ItemisedBillReportService';
import SalespersonsService from 'services/SalesPersonService';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import SyncIcon from '@mui/icons-material/Sync';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import { saveAs } from 'file-saver';
import 'date-fns';
import ItemSummaryReportService from 'services/ItemSummaryReportService';
import Info from 'components/utils/Alert/Info';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import AlertHelper from 'helpers/AlertHelper';
import Error from 'components/utils/Alert/Error';
import { ShopContext } from 'Context/ShopContext';
import { AlertMessagesContext } from 'react-alert-messages';
import CsvHelper from 'helpers/CsvHelper';
import TallyXmlHelper from 'helpers/TallyXmlHelper';
import { filterEndDate } from 'components/utils/FilterEndDate';
import PrintIcon from '@mui/icons-material/Print';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import TerminalService from 'services/TerminalServices';
import { SelectFilterStyle, PRINT_TYPES } from 'const';
import { useReportPrint } from 'hooks/useReportPrint';
import PrintDocument from 'components/utils/PrintDocument/index';
import DateLimitDialog from 'components/popups/DateLimitDialog';
import ItemSearchSelect from 'components/utils/ItemSearchSelect/ItemSearchSelect';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import { InventoryContext } from 'Context/InventoryContext';
import { useTranslation } from 'react-i18next';

function ItemisedBillSummaryReport() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { categories, getCategories } = useContext(InventoryContext);

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState(null);

  const handleExportMenuOpen = (event) => {
    setExportMenuAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchorEl(null);
  };
  const [filterFromDate, setFilterFromDate] = useState(getDefaultStartTime());
  const [filterToDate, setFilterToDate] = useState(filterEndDate());
  const [terminals, setTerminals] = useState([]);
  const [filterTerminal, setFilterTerminal] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [filterSalesperson, setFilterSalesperson] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterTax, setFilterTax] = useState('all');
  const [filterItem, setFilterItem] = useState('All');
  const [sorting, setSorting] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [taxes, setTaxes] = useState('');
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
    filterItem,
    filterCategory,
    sorting,
    filterTax,
  });
  const [errorMsg, setErrorMsg] = useState();
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [dateLimitPopup, setDateLimitPopup] = useState(false);
  const { reportToPrint, handlePrint: handlePrintReportHook } = useReportPrint(shop, tCommon, tReport, DateTimeHelpers);

  const terminalMap = useMemo(() => {
    return Object.fromEntries((terminals || []).map((terminal) => [terminal.id, terminal.name]));
  }, [terminals]);

  const salesPersonMap = useMemo(() => {
    return Object.fromEntries((salesPersons || []).map((person) => [person.id, person.name]));
  }, [salesPersons]);

  const CSV_COLUMNS = {
    bill_no: {
      title: tCommon('Bill No'),
      getValue: (datum) => datum?.bill_no || datum.fid,
    },
    plu: tCommon('PLU'),
    name: tCommon('Name'),
    date: tCommon('Date'),
    time: {
      title: tCommon('Time'),
      getValue: (datum) => DateTimeHelpers.formatServerTimeSegment(datum.time),
    },
    price: {
      title: tCommon('Unit Price'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.price || 0);
      },
    },
    tax: tCommon('Tax(%)'),
    tax_amount: {
      title: tCommon('Tax Amount'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.tax_amount || 0);
      },
    },
    cess: tCommon('Additional Tax(%)'),
    cess_amount: {
      title: tCommon('Additional Tax'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.cess_amount || 0);
      },
    },
    quantity: tCommon('Sold Quantity'),
    total: {
      title: tCommon('Total Amount'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.total || 0);
      },
    },
  };

  function getDefaultStartTime() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return (
      filterFromDate !== prevFilterData.filterFromDate ||
      filterToDate !== prevFilterData.filterToDate ||
      filterItem !== prevFilterData.filterItem ||
      filterCategory !== prevFilterData.filterCategory ||
      sorting !== prevFilterData.sorting ||
      filterTax !== prevFilterData.filterTax ||
      filterTerminal !== prevFilterData.filterTerminal ||
      filterSalesperson !== prevFilterData.filterSalesperson
    );
  }, [
    filterFromDate,
    filterToDate,
    filterItem,
    filterCategory,
    sorting,
    filterTax,
    filterTerminal,
    filterSalesperson,
    prevFilterData.filterFromDate,
    prevFilterData.filterToDate,
    prevFilterData.filterItem,
    prevFilterData.filterCategory,
    prevFilterData.sorting,
    prevFilterData.filterTax,
    prevFilterData.filterTerminal,
    prevFilterData.filterSalesperson,
  ]);

  useEffect(() => {
    generateItemisedBillReports();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    getVat();
    if (ShopsHelper.hasPremiumAccess(shop)) {
      getSalespersons();
    }
    getTerminals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isShowGenerateButton, isAnyChangeOnReportsFilters]);

  const handlePrintReport = () => {
    handlePrintReportHook(
      tReport('Itemised Bill Summary Report'),
      filterFromDate,
      filterToDate,
      headerData,
      filteredReportData,
      {
        [tCommon('Total Tax')]: `${shop?.currency || ''} ${summary.totalTax}`,
        [tCommon('Total Additional Tax')]: `${shop?.currency || ''} ${summary.totalAdditionalTax}`,
        [tCommon('Total Amount')]: `${shop?.currency || ''} ${summary?.totalAmount}`,
      }
    );
  };

  const generateItemisedBillReports = () => {
    const { startDate, endDate } = dynamicDateRange('ITEMISED_BILL');

    setFilterFromDate(startDate);
    setFilterToDate(endDate);

    loadItemisedBillReports({
      startTime: startDate,
      endTime: endDate,
      sort: sorting,
      category: filterCategory,
      item: filterItem,
      vat: filterTax,
      cashier_ids: filterSalesperson,
      terminal_ids: filterTerminal,
    });

    setIsShowGenerateButton(false);
    setPrevFilterData({
      filterFromDate: startDate,
      filterToDate: endDate,
      filterItem,
      filterCategory,
      sorting,
      filterTax,
      filterSalesperson,
      filterTerminal,
    });
  };

  const getSalespersons = async () => {
    const res = await SalespersonsService.getSalesPersons();
    setSalesPersons(res);
  };

  const getTerminals = async () => {
    const res = await TerminalService.getTerminals();
    setTerminals(res);
  };

  const getVat = async () => {
    const res = await ItemSummaryReportService.getVat();
    setTaxes(res);
  };

  const toCsvBtnPressed = async () => {
    const titleLine = tReport('Item Summary Report for {{from}} to {{to}}', {
      from: DateTimeHelpers.convertDateToDMY(filterFromDate),
      to: DateTimeHelpers.convertDateToDMY(filterToDate),
    });

    const csvWithoutTotal = CsvHelper.getString(filteredReportData, CSV_COLUMNS, titleLine);

    // Create total row
    const summary = _getItemisedReportSummary(filteredReportData);
    const fields = Object.keys(CSV_COLUMNS);
    const totalRowValues = fields.map((key) => {
      switch (key) {
        case 'name':
          return tCommon('Total');
        case 'tax_amount':
          return summary.totalTax;
        case 'cess_amount':
          return summary.totalAdditionalTax;
        case 'total':
          return summary.totalAmount;
        default:
          return '';
      }
    });

    const totalRow = totalRowValues.join(',') + '\n';
    const csv = csvWithoutTotal + '\n' + totalRow;

    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `ITEMISED-${dateTime}.csv`);

    postAlertMessage({
      text: tReport('Exported to excel successfully'),
      type: 'success',
    });
  };

  const toTallyXmlBtnPressed = () => {
    if (!filteredReportData || filteredReportData.length === 0) {
      postAlertMessage({ text: tReport('No data available to export'), type: 'failed' });
      return;
    }

    try {
      const billMap = {};
      filteredReportData.forEach((row) => {
        const key = row.bill_no || row.fid || 'UNKNOWN';
        if (!billMap[key]) {
          billMap[key] = {
            bill_no: key,
            date: row.date,
            time: row.time,
            customer_name: row.customer_name || 'Local Sales',
            items: [],
            total: 0,
            tax_amount: 0,
            cess_amount: 0,
          };
        }
        const itemTotal = parseFloat(row.total || 0);
        const itemTax = parseFloat(row.tax_amount || 0);
        const itemCess = parseFloat(row.cess_amount || 0);
        billMap[key].total += itemTotal;
        billMap[key].tax_amount += itemTax;
        billMap[key].cess_amount += itemCess;
        billMap[key].items.push({
          name: row.name,
          quantity: row.quantity || row.option_quantity || 1,
          rate: row.price || row.rate || 0,
          payable: itemTotal - itemTax - itemCess,
          tax: row.tax || 0,
          unit: row.unit || 'NOS',
          hsn: row.plu || row.hsn || '',
        });
      });

      const salesList = Object.values(billMap);
      const xmlContent = TallyXmlHelper.generateTallySalesXml(salesList, {
        companyName: shop?.name || shop?.company_name || 'MACHAD MILL',
        state: shop?.state || 'Kerala',
        gstin: shop?.tax_number || shop?.gstin || shop?.config?.gstin || '32AAMFL4079J1Z0',
      });

      const blob = new Blob([xmlContent], { type: 'application/xml;charset=utf-8' });
      const dateTime = DateTimeHelpers.getDate(new Date());
      saveAs(blob, `TALLY-ITEMISED-SALES-${dateTime}.xml`);

      postAlertMessage({
        text: tReport('Exported to Tally XML successfully'),
        type: 'success',
      });
    } catch (err) {
      postAlertMessage({
        text: err?.message || tReport('Failed to export Tally XML'),
        type: 'failed',
      });
    }
  };

  const loadItemisedBillReports = async ({
    startTime = filterFromDate,
    endTime = filterToDate,
    sort = sorting,
    category = filterCategory,
    item = filterItem,
    tax = filterTax,
    cashier_ids = filterSalesperson,
    terminal_ids = filterTerminal,
  } = {}) => {
    if (DateTimeHelpers.reportDateLimit(startTime, endTime)) {
      setDateLimitPopup(true);
      return;
    }

    setLoadingIndicator(true);
    try {
      const data = {
        filter_start_time: DateTimeHelpers.getTimezoneOffsetDate(startTime),
        filter_end_time: DateTimeHelpers.getTimezoneOffsetDate(endTime),
        sort_bill_num: sort,
        category_id: category === 'all' ? undefined : category,
        item_id: item === 'All' || item === 'all' ? undefined : item,
        filter_tax: tax === 'all' ? undefined : [tax],
        terminal_ids: terminal_ids.length ? terminal_ids : null,
        cashier_ids: cashier_ids.length ? cashier_ids : null,
      };

      const resp = await ItemisedBillReportService.getItemisedBillReport(data);
      setReportData(resp);
    } catch (err) {
      AlertHelper.setShortMessage(setErrorMsg, err.message);
    } finally {
      setLoadingIndicator(false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const headerData = [
    {
      label: tCommon('Bill No'),
      id: 'bill_no',
      type: 'callback',
      viewRender: (obj) => obj.bill_no || obj.fid,
    },
    {
      label: tCommon('PLU'),
      id: 'plu',
      type: 'text',
    },
    {
      label: tCommon('Name'),
      id: 'name',
      type: 'callback',
      viewRender: (item) => `${item.name} ${item.option_label ? `- ${item.option_label}` : ''}`,
    },
    {
      label: tCommon('Date'),
      id: 'date',
      type: 'text',
    },
    {
      label: tCommon('Time'),
      id: 'time',
      type: 'callback',
      viewRender: (item) => DateTimeHelpers.formatServerTimeSegment(item.time),
    },
    {
      label: tCommon('Unit Price'),
      id: 'price',
      type: 'floatAmount',
    },
    {
      label: tCommon('Tax(%)'),
      id: 'tax',
      type: 'text',
    },
    {
      label: tCommon('Tax Amount'),
      id: 'tax_amount',
      type: 'floatAmount',
    },
    {
      label: tCommon('Additional Tax(%)'),
      id: 'cess',
      type: 'text',
    },
    {
      label: tCommon('Additional Tax'),
      id: 'cess_amount',
      type: 'floatAmount',
    },
    {
      label: tCommon('Sold Quantity'),
      id: 'quantity',
      type: 'callback',
      viewRender: (item) => {
        if (!item.quantity || isNaN(item.quantity)) return;
        return `${ShopsHelper.getAmountFormatted(shop, item.option_quantity ? item.option_quantity : item.quantity)} ${
          item.unit || ''
        }`;
      },
    },
    {
      label: tCommon('Total Amount'),
      id: 'total',
      type: 'callback',
      viewRender: (item) => {
        return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, item?.total)}`;
      },
    },
  ];

  const _getItemisedReportSummary = (items) => {
    let totalAmount = 0;
    let totalTax = 0;
    let totalAdditionalTax = 0;

    const salesItems = items.filter(
      (item) => item.name?.toLowerCase() !== 'discount' && String(item.plu) !== '9999996'
    );

    salesItems.forEach((item) => {
      totalAmount += item?.total;
      totalTax += item.tax_amount;
      totalAdditionalTax += item.cess_amount;
    });

    return {
      totalAmount: ShopsHelper.getAmountFormatted(shop, totalAmount),
      totalTax: ShopsHelper.getAmountFormatted(shop, totalTax),
      totalAdditionalTax: ShopsHelper.getAmountFormatted(shop, totalAdditionalTax),
    };
  };

  const filteredReportData = reportData.filter(
    (datum) =>
      datum.bill_no?.toLowerCase()?.includes(searchText.toLowerCase()) ||
      datum.name?.toLowerCase()?.includes(searchText.toLowerCase())
  );
  const summary = _getItemisedReportSummary(filteredReportData);

  useEffect(() => {
    getCategories();
  }, [getCategories]);
  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <span className={styles.title}>
          {' '}
          {tCommon('Reports')}
          <span className={styles.menuTitle}>{tCommon('Generation')}</span>
        </span>
        <div style={{ justifyContent: 'flex-end', marginRight: '10px' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Print Report')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={handlePrintReport}
          >
            <PrintIcon className={styles.actionBtnIcon} />
            {tCommon('Print')}
          </Button>
        </div>
        <div style={{ justifyContent: 'flex-end' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Export As')}</label>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              className={styles.actionBtn}
              style={{ backgroundColor: '#00a65a' }}
              onClick={toCsvBtnPressed}
            >
              <ImportExportIcon className={styles.actionBtnIcon} />
              {tCommon('CSV')}
            </Button>
            <IconButton
              onClick={handleExportMenuOpen}
              style={{
                backgroundColor: '#0284c7',
                color: '#fff',
                borderRadius: '4px',
                width: '40px',
                height: '40px',
              }}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={exportMenuAnchorEl}
              open={Boolean(exportMenuAnchorEl)}
              onClose={handleExportMenuClose}
            >
              <MenuItem
                onClick={() => {
                  handleExportMenuClose();
                  handlePrintReport();
                }}
              >
                <PrintIcon style={{ marginRight: '8px', fontSize: '20px' }} />
                {tCommon('Print')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleExportMenuClose();
                  toCsvBtnPressed();
                }}
              >
                <ImportExportIcon style={{ marginRight: '8px', fontSize: '20px' }} />
                {tCommon('CSV')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleExportMenuClose();
                  toTallyXmlBtnPressed();
                }}
              >
                <ImportExportIcon style={{ marginRight: '8px', fontSize: '20px' }} />
                {tCommon('Tally XML')}
              </MenuItem>
            </Menu>
          </div>
        </div>
      </div>
      <div className={styles.changeable}>
        <div className={styles.filterSec}>
          <div className={styles.headTitle}>
            <h2 className={styles.subTitle}>
              {tReport('Sale Item Summary Report for {{from}} to {{to}}', {
                from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                to: DateTimeHelpers.convertDateToDMY(filterToDate),
              })}
            </h2>
          </div>
          <div className={styles.filerInputSec}>
            <div className={styles.searchSec}>
              <input
                type="text"
                value={searchText}
                onChange={(e) => {
                  handleSearch(e.target.value);
                }}
                className={styles.searchInput}
                placeholder={tReport('Search by bill no, date')}
              />
              <Tooltip title="Search" placement="bottom">
                <SearchIcon className={styles.searchIcon} />
              </Tooltip>
            </div>
          </div>
        </div>
        <div className={styles.actionButtons}>
          <div className={styles.dateDiv}>
            <DateTimeRange
              setFilterFromDate={setFilterFromDate}
              setFilterToDate={setFilterToDate}
              storageKey="ITEMISED_BILL"
            />
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Terminal')}</label>
            </div>
            <div>
              <FormControl>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  multiple
                  displayEmpty
                  value={filterTerminal}
                  onChange={(e) => setFilterTerminal(e.target.value)}
                  input={<OutlinedInput />}
                  style={{ width: 135, height: 40 }}
                  renderValue={(filterTerminal) => {
                    if (filterTerminal.length === 0) return <span>{tCommon('All')}</span>;
                    const selectedNames = filterTerminal.map((id) => terminalMap[id]).filter(Boolean);
                    return selectedNames.join(', ');
                  }}
                  MenuProps={SelectFilterStyle}
                >
                  {terminals.map((terminal) => (
                    <MenuItem key={terminal.id} value={terminal.id}>
                      <Checkbox checked={filterTerminal.includes(terminal.id)} color="primary" />
                      <ListItemText primary={terminal.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          {ShopsHelper.hasPremiumAccess(shop) && (
            <div className={styles.filterDiv}>
              <div style={{ paddingBottom: '4px' }}>
                <label className={styles.label}>{tCommon('Salesperson')}</label>
              </div>
              <div>
                <FormControl>
                  <Select
                    labelId="demo-multiple-checkbox-label"
                    id="demo-multiple-checkbox"
                    multiple
                    displayEmpty
                    value={filterSalesperson}
                    onChange={(e) => setFilterSalesperson(e.target.value)}
                    input={<OutlinedInput />}
                    style={{ width: 135, height: 40 }}
                    renderValue={(filterSalesperson) => {
                      if (filterSalesperson.length === 0) return <span>{tCommon('All')}</span>;
                      const selectedNames = filterSalesperson.map((id) => salesPersonMap[id]).filter(Boolean);
                      return selectedNames.join(', ');
                    }}
                    MenuProps={SelectFilterStyle}
                  >
                    {salesPersons.map((salesperson) => (
                      <MenuItem key={salesperson.id} value={salesperson.id}>
                        <Checkbox checked={filterSalesperson.includes(salesperson.id)} color="primary" />
                        <ListItemText primary={salesperson?.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>
          )}
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Tax')}</label>
            </div>
            <div>
              <TextField
                select
                name="category"
                size="small"
                variant="outlined"
                defaultValue={filterTax}
                color="primary"
                className={styles.textfeild}
                onChange={(event) => {
                  setFilterTax(event.target.value);
                }}
              >
                {taxes &&
                  taxes.map((vat) => (
                    <MenuItem value={vat} key={vat}>
                      {vat}
                    </MenuItem>
                  ))}
                <MenuItem value="all">{tCommon('All')}</MenuItem>
              </TextField>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Category')}</label>
            </div>
            <div>
              <TextField
                select
                name="filterTax"
                size="small"
                variant="outlined"
                defaultValue={filterCategory}
                color="primary"
                className={styles.textfeild}
                onChange={(event) => {
                  setFilterCategory(event.target.value);
                }}
              >
                {categories?.map((category) => (
                  <MenuItem value={category.id} key={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
                <MenuItem value="all">{tCommon('All')}</MenuItem>
              </TextField>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Item')}</label>
            </div>
            <ItemSearchSelect filterItem={filterItem} setFilterItem={setFilterItem} />
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Bill no order')}</label>
            </div>
            <div>
              <TextField
                select
                name="billNo"
                size="small"
                variant="outlined"
                color="primary"
                style={{ width: 135 }}
                value={sorting}
                onChange={(event) => {
                  setSorting(event.target.value);
                }}
                displayEmpty
              >
                <MenuItem value={null}>
                  <em>None</em>
                </MenuItem>
                <MenuItem value={'DESC'}>Descending</MenuItem>
                <MenuItem value={'ASC'}>Ascending</MenuItem>
              </TextField>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Generate Report')}</label>
            </div>
            <div>
              <Button
                variant="contained"
                color="primary"
                className={styles.actionBtn}
                style={{ backgroundColor: '#00a65a' }}
                onClick={generateItemisedBillReports}
                disabled={isShowGenerateButton === false || loadingIndicator}
              >
                {loadingIndicator ? (
                  <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                ) : (
                  <SyncIcon className={styles.actionBtnIcon} />
                )}
                {tCommon('Generate')}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {errorMsg && (
        <div className={styles.marginTop}>
          <Error title={errorMsg} />
        </div>
      )}

      {isShowGenerateButton === null ? (
        <Info
          title={tReport('Set filters and click generate button')}
          content={tReport(
            'Reports are generated based on the filters. Please set filters and click generate button to generate the report.'
          )}
        />
      ) : filteredReportData && filteredReportData.length ? (
        <>
          <div className={styles.summaryContainer}>
            <div className={`${styles.summaryCard} ${styles.cardBlue}`}>
              <span className={styles.cardTitle}>{tCommon('Total Amount')}</span>
              <div className={styles.cardValue}>{`${shop?.currency || ''} ${summary.totalAmount}`}</div>
            </div>

            <div className={`${styles.summaryCard} ${styles.cardGreen}`}>
              <span className={styles.cardTitle}>{tCommon('Total Items')}</span>
              <div className={styles.cardValue}>{filteredReportData.length}</div>
            </div>

            <div className={`${styles.summaryCard} ${styles.cardOrange}`}>
              <span className={styles.cardTitle}>{tCommon('Tax Amount')}</span>
              <div className={styles.cardValue}>{`${shop?.currency || ''} ${summary.totalTax}`}</div>
            </div>

            <div className={`${styles.summaryCard} ${styles.cardPurple}`}>
              <span className={styles.cardTitle}>{tCommon('Additional Tax')}</span>
              <div className={styles.cardValue}>{`${shop?.currency || ''} ${summary.totalAdditionalTax}`}</div>
            </div>
          </div>

          <div className={styles.tableContainer}>
            <Grayout open={isShowGenerateButton} />
            <DataTable
              columns={headerData}
              rows={filteredReportData}
              rowKey="bill_no-order"
              summary={{
                name: 'Total',
                total: `${shop?.currency || ''} ${summary?.totalAmount}`,
                tax_amount: `${shop?.currency || ''} ${summary.totalTax}`,
                cess_amount: `${shop?.currency || ''} ${summary.totalAdditionalTax}`,
              }}
              pagination={true}
            />
          </div>
        </>
      ) : (
        !loadingIndicator && (
          <Info
            severity="warning"
            title={tReport('Oh no, there are no sales matching to your filter')}
            content={tReport(
              'There are no sales matching to your filter. Please verify your inputs to generate report.'
            )}
          />
        )
      )}

      {dateLimitPopup && (
        <DateLimitDialog
          handleClose={() => {
            setDateLimitPopup(false);
          }}
        />
      )}
      {reportToPrint &&
        createPortal(<PrintDocument type={PRINT_TYPES.REPORT} data={reportToPrint} shop={shop} />, document.body)}
    </div>
  );
}

export default withConsoleBase(ItemisedBillSummaryReport);
